import { MagicWand } from '@phosphor-icons/react';
import { EventSource } from 'eventsource';
import { getAuth } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { PromptTextArea } from 'src/components/PromptTextArea';
import { Textarea } from 'src/components/Textarea';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { ProviderSelect } from 'src/pages/Automations/components/ui/ProviderSelect';
import { CRM_PROVIDER_OPTIONS } from 'src/pages/Automations/components/wizard/WizardContext';
import { cn } from 'src/utils/className';
import { extractVariables } from 'src/utils/extractVariables';

const PROMPT_STREAM_ENDPOINT = `${process.env.REACT_APP_BASE_API_URL}/prompt-builder/chat/stream`;
const PROMPT_STREAM_ENDPOINT_CHAT = `${process.env.REACT_APP_BASE_API_URL}/prompt-builder/interactive_chat/stream`;

// Variables available for call analysis prompt that map to Call fields
// Keep names in single-brace form to match current editors
// Ordered by most useful first per UX feedback
export const CALL_ANALYSIS_PROMPT_VARIABLES: string[] = [
  '{transcript}',
  '{human_detection_result}',
  '{contact_number}',
  '{agent_number}',
  '{from_number}',
  '{to_number}',
  '{status}',
  '{recording_available}',
  '{stage}',
  '{stage_outcome}',
  '{start_time}',
  '{end_time}',
  '{errors}',
  '{do_not_call_result}',
  '{telephony_provider}',
  '{telephony_account_connection}',
  '{is_outgoing}',
  '{telephony_id}',
  '{telephony_metadata}',
  '{id}',
  '{user_id}',
];

type PostCallPromptSectionProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading?: boolean;
};
export const PostCallPromptSection = ({
  agent,
  setAgent,
  loading = false,
}: PostCallPromptSectionProps) => {
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [promptGenerating, setPromptGenerating] = useState(false);
  const [promptStreaming, setPromptStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [testCallModalOpen, setTestCallModalOpen] = useState(false);
  const [testCallId, setTestCallId] = useState('');
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [testResults, setTestResults] = useState('');
  const [generatePromptModalOpen, setGeneratePromptModalOpen] = useState(false);
  const [promptGeneratorInput, setPromptGeneratorInput] = useState('');
  const [token, setToken] = useState<string | undefined>();

  const { get, post } = useAuthedRequest();

  const { environment } = useEnvironment();
  const environmentId = environment?.envId;
  const notification = useNotification();

  const searchPrompts = useCallback(
    async (content: string) => {
      const queryObject: QueryObject = {
        filters: JSON.stringify({
          ...(content.length !== 0 && { content }),
          type: 'prompt',
          scope: 'global|user',
        }),
      };
      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data = await get(`/prompts?${query.toString()}`);

      return data.items.map((prompt: Prompt) => ({
        value: prompt.id,
        label: prompt.content,
      }));
    },
    [get],
  );

  useEffect(() => {
    async function fetchToken() {
      const token = await getAuth().currentUser?.getIdToken();
      setToken(token);
    }

    fetchToken();
  }, []);

  const populatePromptContent = async (promptId: string) => {
    if (!promptId) return;

    setLoadingPrompt(true);

    try {
      const post_call_prompt = await get(`/prompts/${promptId}`);
      setAgent((prevAgent) => ({
        ...prevAgent,
        post_call_prompt: post_call_prompt.content,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPrompt(false);
    }
  };

  const handleTestCallId = async () => {
    if (!agent.post_call_prompt) {
      return;
    }

    try {
      setGeneratingResponse(true);
      setTestResults('');

      const call = await get(`/calls/${testCallId}`);
      if (!call) {
        notification.error('Call not found');
        setGeneratingResponse(false);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const variableMap: Record<string, any> = {
        '{transcript}': call.transcript || '',
        '{human_detection_result}': call.human_detection_result || '',
        '{contact_number}': call.is_outgoing
          ? call.to_number
          : call.from_number,
        '{agent_number}': call.is_outgoing ? call.from_number : call.to_number,
        '{from_number}': call.from_number || '',
        '{to_number}': call.to_number || '',
        '{status}': call.status || '',
        '{recording_available}': String(call.recording_available ?? false),
        '{stage}': call.stage || '',
        '{stage_outcome}': call.stage_outcome || '',
        '{start_time}': call.start_time || '',
        '{end_time}': call.end_time || '',
        '{errors}': Array.isArray(call.errors)
          ? call.errors.join(',')
          : call.errors || '',
        '{do_not_call_result}': String(call.do_not_call_result ?? false),
        '{telephony_provider}': call.telephony_provider
          ? JSON.stringify(call.telephony_provider)
          : '',
        '{telephony_account_connection}': call.telephony_account_connection
          ? JSON.stringify(call.telephony_account_connection)
          : '',
        '{is_outgoing}': String(call.is_outgoing ?? false),
        '{telephony_id}': call.telephony_id || '',
        '{telephony_metadata}': call.telephony_metadata
          ? JSON.stringify(call.telephony_metadata)
          : '',
        '{id}': call.id || '',
        '{user_id}': call.user_id || '',
      };

      let promptWithData = agent.post_call_prompt;
      CALL_ANALYSIS_PROMPT_VARIABLES.forEach((variable) => {
        promptWithData = promptWithData.replaceAll(
          variable,
          variableMap[variable] ?? '',
        );
      });

      const query = new URLSearchParams({
        last_message: promptWithData,
        conversation_id: conversationId || '',
      });

      let completeResult = '';
      const eventSource = new EventSource(
        `${PROMPT_STREAM_ENDPOINT_CHAT}?${query.toString()}`,
        {
          fetch: (input, init) =>
            fetch(input, {
              ...init,
              headers: {
                ...init?.headers,
                Authorization: `Bearer ${token}`,
                'x-env': environment?.envId || '',
              },
            }),
        },
      );

      eventSource.onmessage = (event) => {
        const response = JSON.parse(event.data);

        if (response.type === 'completed') {
          eventSource.close();
          setGeneratingResponse(false);
          return;
        }

        if (response.text) {
          completeResult += response.text;
          setTestResults(completeResult);
          setTestCallId('');
        }
      };

      eventSource.onerror = () => {
        notification.error('Failed to test call ID');
        eventSource.close();
        setGeneratingResponse(false);
      };
    } catch (error) {
      notification.error('Failed to test call ID');
      console.error(error);
      setGeneratingResponse(false);
    }
  };

  useEffect(() => {
    const fetchConversationId = async () => {
      if (conversationId) return;
      const response = await post('/prompt-builder/conversations', {});
      setConversationId(response.id);
    };
    fetchConversationId();
  }, [conversationId, post]);

  return (
    <div className={cn(loading && 'opacity-50')}>
      <h4>Analyze Call</h4>

      <div className="w-full mt-2">
        <AutoCompleteSelectBox
          fetchOptions={searchPrompts}
          onChange={(value) => populatePromptContent(value)}
          className="w-full"
          variant="outlined"
          color="primary"
          size="medium"
          disabled={loading || (!agent.user_id && !!agent.id)}
        />
      </div>

      <div className="col-span-2 mt-4">
        <div className="relative">
          <PromptTextArea
            value={agent.post_call_prompt ?? ''}
            onChange={(e) =>
              setAgent({ ...agent, post_call_prompt: e.target.value })
            }
            loading={
              loading || loadingPrompt || promptGenerating || promptStreaming
            }
            variables={Array.from(
              new Set([
                ...CALL_ANALYSIS_PROMPT_VARIABLES,
                ...extractVariables(agent.post_call_prompt ?? ''),
              ]),
            )}
            placeholder={
              'Example: Summarize the key points discussed, extract contact information, generate follow-up actions, or reformat data as needed. All call fields are accessible via variables (i.e. {transcript})'
            }
            rows={12}
            autoScroll={promptStreaming}
            className={cn(
              'resize-none',
              promptStreaming && 'opacity-50',
              promptGenerating && 'opacity-50',
              loadingPrompt && 'opacity-50',
            )}
          />
          {promptGenerating && (
            <div className="absolute top-0 right-0 w-full h-full bg-neutral-0 flex flex-col justify-center items-center">
              <div className="flex flex-col justify-center items-center gap-2">
                <Loading size="16" className="overflow-hidden" />
                <p>Generating prompt...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full mt-4">
        <div className="flex gap-1">
          <div>Write results to: </div>
        </div>
        <div>
          <ProviderSelect
            key={agent.id}
            label=""
            htmlFor="provider"
            providers={CRM_PROVIDER_OPTIONS}
            selectedProvider={{
              value:
                CRM_PROVIDER_OPTIONS.find(
                  (option) =>
                    option.value ===
                    agent?.activity_actions?.filter(
                      (action) => action.type === 'write_analysis_activity',
                    )[0]?.config?.destination_id,
                )?.value ?? '',
              label:
                CRM_PROVIDER_OPTIONS.find(
                  (option) =>
                    option.value ===
                    agent?.activity_actions?.filter(
                      (action) => action.type === 'write_analysis_activity',
                    )[0]?.config?.destination_id,
                )?.label ?? 'Select a provider',
            }}
            onChange={(e) => {
              if (!agent) return;

              setAgent({
                ...agent,
                activity_actions: [
                  {
                    type: 'write_analysis_activity',
                    config: {
                      provider_type: 'integration_app',
                      destination_id: e.value,
                    },
                  },
                ],
              });
            }}
          />
        </div>
      </div>

      <div className="mb-10">
        <Button
          color="primary"
          onClick={() => setTestCallModalOpen(true)}
          disabled={!agent.post_call_prompt}
          size="small"
          className="flex items-center gap-1"
        >
          <MagicWand size={20} />
          Test Analysis With Call ID
        </Button>
      </div>

      <Modal
        title="Generate Agent Prompt"
        isOpen={generatePromptModalOpen}
        onClose={() => setGeneratePromptModalOpen(false)}
        actionButton={
          <Button
            onClick={async () => {
              setPromptGenerating(true);
              setGeneratePromptModalOpen(false);
              let completeMessage = '';
              const query = new URLSearchParams({
                last_message: promptGeneratorInput,
              });
              const eventSource = new EventSource(
                `${PROMPT_STREAM_ENDPOINT}?${query.toString()}`,
                {
                  // Pass auth for SSE
                  fetch: (input: RequestInfo | URL, init: RequestInit) =>
                    fetch(input, {
                      ...init,
                      headers: {
                        ...init?.headers,
                        Authorization: `Bearer ${token}`,
                        'x-env': environmentId || '',
                      },
                    }),
                },
              );
              eventSource.onmessage = (event) => {
                setPromptGenerating(false);
                const response = JSON.parse(event.data);
                if (response.type === 'completed') {
                  eventSource.close();
                  setPromptStreaming(false);
                  return;
                }
                setPromptStreaming(true);
                completeMessage += response.text;
                setAgent({ ...agent, post_call_prompt: completeMessage });
              };
              eventSource.onerror = () => {
                notification.error(
                  'Failed to generate prompt. Please wait a minute and try again.',
                );
                eventSource.close();
                setPromptStreaming(false);
                setPromptGenerating(false);
              };
              setPromptGeneratorInput('');
            }}
          >
            Generate
          </Button>
        }
      >
        <Textarea
          placeholder={`What do you want this agent to do, including its role, specific tasks, and overall objective with as much detail as possible?`}
          value={promptGeneratorInput}
          onChange={(e) => setPromptGeneratorInput(e.target.value)}
          rows={6}
          className="resize-none"
        />
      </Modal>

      <Modal
        title="Test Analysis"
        isOpen={testCallModalOpen}
        onClose={() => {
          setTestCallModalOpen(false);
          setTestResults('');
          setTestCallId('');
        }}
        className="w-[600px]"
        actionButton={
          <Button
            color="primary"
            size="small"
            onClick={handleTestCallId}
            disabled={
              generatingResponse ||
              testCallId.length === 0 ||
              !conversationId ||
              !agent.post_call_prompt
            }
          >
            {generatingResponse ? 'Testing...' : 'Test'}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <div>
            <Input
              placeholder="Enter the call ID to test"
              value={testCallId}
              onChange={(e) => setTestCallId(e.target.value)}
              disabled={generatingResponse}
              className="w-full"
            />
          </div>

          {testResults && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Extraction Results
              </label>
              <div className="relative">
                <Textarea
                  value={testResults}
                  readOnly
                  rows={12}
                  className="resize-none font-mono text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
